import React, { useState } from 'react';
import { Modal, Form, Input, Button, Upload, message, Spin, InputNumber, Radio, Switch } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useAddEventMutation } from '@/services/api/event/eventApi';
import { uploadToCloudinary } from '@/utils/cloudinaryUpload'; // ✅ Cloudinary helper

const { TextArea } = Input;

const AddEventModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [servingType, setServingType] = useState('range'); // 'range' or 'fixed'
  const [uploading, setUploading] = useState(false);

  const [addEvent, { isLoading }] = useAddEventMutation();

  // ✅ Handle Cloudinary upload
  const handleUpload = async (file) => {
    try {
      setUploading(true);
      message.loading({ content: 'Uploading image...', key: 'upload' });
      const url = await uploadToCloudinary(file);
      setImageUrl(url);
      message.success({ content: 'Image uploaded successfully!', key: 'upload' });
    } catch (error) {
      console.error('Cloudinary upload failed:', error);
      message.error({ content: 'Failed to upload image', key: 'upload' });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadChange = async ({ file }) => {
    if (file.status !== 'removed' && file.originFileObj) {
      await handleUpload(file.originFileObj);
    } else if (file.status === 'removed') {
      setImageUrl('');
    }
  };

  // ✅ Submit form
  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      let minimumServingSize, maximumServingSize;

      if (servingType === 'fixed') {
        minimumServingSize = values.fixed_serving_size || 0;
        maximumServingSize = values.fixed_serving_size || 0;
      } else {
        minimumServingSize = values.minimum_serving_size || 0;
        maximumServingSize = values.maximum_serving_size || 0;
      }

      const payload = {
        name: values.name,
        minimum_serving_size: minimumServingSize,
        maximum_serving_size: maximumServingSize,
        url: imageUrl || '',
        status: values.active ? 1 : 0,
        description: values.description,
      };

      await addEvent(payload).unwrap();
      message.success('Package created successfully!');
      form.resetFields();
      setImageUrl('');
      setServingType('range');
      onSuccess?.();
      onCancel();
    } catch (error) {
      console.error('Error creating package:', error);
      message.error(error?.data?.message || 'Failed to create package');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setImageUrl('');
    setServingType('range');
    onCancel();
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
      destroyOnClose
      closable={false}
      className="add-package-modal"
      styles={{
        body: {
          padding: '0',
          maxHeight: '70vh',
          overflow: 'hidden',
        },
      }}
    >
      <Spin spinning={isSubmitting || isLoading || uploading}>
        {/* HEADER */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 -mx-6 mb-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Add Package</h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <CloseOutlined className="text-lg" />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="max-h-96 overflow-y-auto px-6">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="space-y-6"
          >
            {/* Package Name */}
            <Form.Item
              name="name"
              label={
                <span className="text-gray-700 font-medium">
                  Package Name<span className="text-red-500 ml-1">*</span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter package name' },
                { min: 2, message: 'Name must be at least 2 characters' },
                { max: 100, message: 'Name must not exceed 100 characters' },
              ]}
            >
              <Input
                placeholder="e.g. Native Essence"
                size="large"
                className="h-12 rounded-lg border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              />
            </Form.Item>
            <Form.Item
              name="description"
              label={
                <span className="text-gray-700 font-medium">
                  Description<span className="text-red-500 ml-1">*</span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter package name' },
                { min: 2, message: 'Name must be at least 2 characters' },
                { max: 100, message: 'Name must not exceed 100 characters' },
              ]}
            >
                <TextArea
              placeholder="Describe the package"
              rows={3}
              size="large"
              className="rounded-lg"
            />
            </Form.Item>

            {/* Number of Guests */}
            <div>
              <label className="text-gray-700 font-medium mb-3 block">
                Number of Guests <span className="text-red-500">*</span>
              </label>

              <Radio.Group
                value={servingType}
                onChange={(e) => setServingType(e.target.value)}
                className="mb-4"
              >
                <Radio value="range" className="text-gray-700">
                  Range
                </Radio>
                <Radio value="fixed" className="text-gray-700 ml-6">
                  Fixed
                </Radio>
              </Radio.Group>

              {servingType === 'range' ? (
                <div className="flex items-center gap-3">
                  <Form.Item
                    name="minimum_serving_size"
                    rules={[
                      { required: true, message: 'Please enter minimum serving size' },
                      { type: 'number', min: 0, message: 'Minimum serving size must be 0 or greater' },
                    ]}
                    className="mb-0"
                  >
                    <InputNumber
                      placeholder="0"
                      size="large"
                      className="w-24 h-12 rounded-lg border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                      min={0}
                    />
                  </Form.Item>
                  <span className="text-gray-500 font-medium">to</span>
                  <Form.Item
                    name="maximum_serving_size"
                    rules={[
                      { required: true, message: 'Please enter maximum serving size' },
                      { type: 'number', min: 1, message: 'Maximum serving size must be greater than 0' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const minSize = getFieldValue('minimum_serving_size');
                          if (!value || !minSize || value >= minSize) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Maximum must be >= minimum'));
                        },
                      }),
                    ]}
                    className="mb-0"
                  >
                    <InputNumber
                      placeholder="0"
                      size="large"
                      className="w-24 h-12 rounded-lg border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                      min={1}
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item
                  name="fixed_serving_size"
                  rules={[
                    { required: true, message: 'Please enter serving size' },
                    { type: 'number', min: 1, message: 'Serving size must be greater than 0' },
                  ]}
                  className="mb-0"
                >
                  <InputNumber
                    placeholder="Enter serving size"
                    size="large"
                    className="w-full h-12 rounded-lg border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    min={1}
                  />
                </Form.Item>
              )}
            </div>

            {/* Image Upload */}
            <Form.Item
              label={<span className="text-gray-700 font-medium">Package Image</span>}
            >
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-400 transition-colors bg-gray-50">
                <Upload
                  name="image"
                  listType="picture-card"
                  className="package-uploader w-full"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error('You can only upload image files!');
                      return Upload.LIST_IGNORE;
                    }
                    const isLt2M = file.size / 1024 / 1024 < 2;
                    if (!isLt2M) {
                      message.error('Image must be smaller than 2MB!');
                      return Upload.LIST_IGNORE;
                    }
                    return true;
                  }}
                  onChange={handleUploadChange}
                >
                  {imageUrl ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src={imageUrl}
                        alt="package"
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <div className="text-4xl mb-2">📷</div>
                      <div className="text-sm text-gray-600">
                        Click to Upload or drag and drop
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Max. File Size: 2MB
                      </div>
                    </div>
                  )}
                </Upload>
              </div>
            </Form.Item>

            {/* Active Toggle */}
            <Form.Item
              name="active"
              label={
                <span className="text-gray-700 font-medium">
                  Active (Visible to Customers)
                </span>
              }
              valuePropName="checked"
              initialValue={false}
              className="mb-0"
            >
              <Switch
                className="bg-gray-300"
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </Form.Item>
          </Form>
        </div>

        {/* FOOTER */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 -mx-6 mt-6 z-10">
          <div className="flex justify-end">
            <Button
              type="primary"
              onClick={() => form.submit()}
              size="large"
              loading={isSubmitting || isLoading || uploading}
              className="bg-orange-500 border-orange-500 hover:bg-orange-600 hover:border-orange-600 px-8 h-12 rounded-lg font-medium"
            >
              {isSubmitting || isLoading || uploading ? 'Adding...' : 'Add Package'}
            </Button>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default AddEventModal;
